import React, { useEffect, useCallback, useState } from 'react'
import sweetalert from 'sweetalert2'
import { injectIntl, FormattedMessage } from 'react-intl'
import {
  FormGroup,
  ControlLabel
} from 'react-bootstrap'
import Loader from 'components/Loader'
import withLeftSidebar from 'components/Sidebar/withLeftSidebar'
import {
  LeftSidebarView,
  ViewBody
} from 'components/Sidebar'
import Scrollbars from 'components/Scrollbars'
import Tabs from 'components/Tabs'
import KanBoIcon from 'components/KanBoIcon'
import Toggle from 'components/Toggle'
import LayoutName from './LayoutName'
import LayoutDescription from './LayoutDescription'
import LayoutUsers from './LayoutUsers'
import classes from './LayoutSettings.module.scss'
import CopyLayout from './CopyLayout'

export function LayoutSettings ({
  layoutId,
  addToMyHomeLayout,

  intl,

  leftSidebar,

  currentUserRoles,
  permission,
  layout,

  fetchHomeLayout,
  requestRemoveLayout,
  requestSetLayoutAsPrivate,
  requestSetLayoutAsPublic,
  requestDownloadLayoutTemplate,
}) {
  const [selectedTabId, setSelectedTabId] = useState('general')

  useEffect(() => {
    fetchHomeLayout({
      layoutId
    })
  }, [layoutId])

  const handleCopyClick = useCallback(() => {
    leftSidebar.push(() =>
      <CopyLayout
        addToMyHomeLayout={addToMyHomeLayout}
        layoutId={layoutId}
        layoutName={layout.name}
        layoutIsTemplate={layout.isTemplate}
      />
    )
  }, [addToMyHomeLayout, layout, layoutId, leftSidebar])

  const handleDeleteClick = useCallback(
    () => {
      sweetalert({
        text: intl.formatMessage({
          id: layout.isTemplate ? 'DESK_TEMPLATE_DELETE_QUESTION' : 'DESK_DELETE_QUESTION',
        }, {
          name: layout.name
        }),
        confirmButtonText: intl.formatMessage({ id: 'DELETE' }),
        cancelButtonText: intl.formatMessage({ id: 'CANCEL' }),
        showCancelButton: true,
        type: 'warning',
        confirmButtonClass: 'btn btn-warning'
      }).then(
        () => {
          requestRemoveLayout({ id: layoutId })

          leftSidebar.pop()
        },
        () => {}
      )
    },
    [intl, layout, requestRemoveLayout, layoutId, leftSidebar]
  )

  const handleToggleIsPublicClick = useCallback(
    () => {
      if (layout.isPublic) {
        requestSetLayoutAsPrivate({
          id: layoutId
        })
      } else {
        requestSetLayoutAsPublic({
          id: layoutId
        })
      }
    },
    [layoutId, layout, requestSetLayoutAsPrivate, requestSetLayoutAsPublic]
  )

  const handleDownloadBoardTemplateClick = useCallback(() => {
    requestDownloadLayoutTemplate({
      id: layoutId,
      name: layout.name
    })
  }, [layoutId, layout, requestDownloadLayoutTemplate])

  const tabs = layout ? [{
    id: 'general',
    title: <FormattedMessage id='DESK_GENERAL' />,
    view: (
      <Scrollbars>
        <FormGroup>
          <LayoutName
            layoutId={layoutId}
            name={layout.name}
          />
        </FormGroup>
        <FormGroup>
          <LayoutDescription
            layoutId={layoutId}
            description={layout.description}
          />
        </FormGroup>
        <FormGroup>
          <ViewBody>
            <ControlLabel><FormattedMessage id='DESK_IS_PUBLIC_SETTING' /></ControlLabel>
            <div>
              <Toggle
                disabled={!permission.owner || !currentUserRoles['public-manage']}
                checked={layout.isPublic}
                onChange={handleToggleIsPublicClick}
              />
            </div>
          </ViewBody>
        </FormGroup>
      </Scrollbars>
    )
  }, {
    id: 'users',
    title: <FormattedMessage id='DESK_USERS' />,
    view: (
      <Scrollbars>
        <LayoutUsers
          layoutId={layoutId}
        />
      </Scrollbars>
    )
  }] : []

  return (
    <LeftSidebarView
      title={layout ? <FormattedMessage id={layout.isTemplate ? 'DESK_TEMPLATE_HEADER' : 'DESK_HEADER'} values={{ name: layout.name }} /> : undefined}
      menu={(() => {
        if (!layout) return undefined

        const menu = []

        if (currentUserRoles['board-manage']) {
          menu.push({
            icon: <KanBoIcon name={layout.isTemplate ? 'kanbo-16-copy-board-template' : 'kanbo-16-copy-board-collection'} />,
            title: <FormattedMessage id='DESK_COPY' />,
            onClick: handleCopyClick
          })

          menu.push({
            icon: <KanBoIcon name='kanbo-16-download' />,
            title: <FormattedMessage id={layout.isTemplate ? 'DESK_TEMPLATE_DOWNLOAD_TEMPLATE' : 'DESK_DOWNLOAD_TEMPLATE'} />,
            onClick: handleDownloadBoardTemplateClick
          })
        }

        if (permission.owner) {
          menu.push({
            icon: <KanBoIcon name='kanbo-16-delete' />,
            title: <FormattedMessage id='DESK_DELETE' />,
            onClick: handleDeleteClick
          })
        }

        return menu.length > 0 ? menu : undefined
      })()}
    >
      {layout ? (
        <Tabs
          tabs={tabs}
          defaultSelectedTabId={selectedTabId}
          className={classes.tabs}
          tabContainerProps={{
            className: classes.tabContainer
          }}
          onTabChange={setSelectedTabId}
        />
      ) : (
        <Loader center />
      )}
    </LeftSidebarView>
  )
}

export default injectIntl(withLeftSidebar(LayoutSettings))
